// ✅ src/pages/Sales/FullPaymentCustomer.jsx  (UPDATED FULL FILE)
// Added:
// ✅ Safe localStorage helper
// ✅ localStorage values
// ✅ commonHeaders using useMemo
// ✅ headers passed in all axios API calls

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaEye
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const FullPaymentCustomer = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // dropdown data
  const [assetSubTypes, setAssetSubTypes] = useState([]);

  // table data
  const [rows, setRows] = useState([]);

  // ✅ selected ids
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubTypeId: "",
    doB: "",
  });

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId],
  );

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const getPaymentStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // -------------------------
  // Load Asset Sub Types
  // -------------------------
  const loadAssetSubTypes = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/fullPaymentListing/assetsubtypes`,
        {
          headers: commonHeaders,
        },
      );
      setAssetSubTypes(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Asset Sub Types");
      setAssetSubTypes([]);
    }
  };

  useEffect(() => {
    loadAssetSubTypes();
  }, [commonHeaders]);

  // -------------------------
  // Search
  // -------------------------
  const handleSearch = async () => {
    const hasSearchCriteria = Object.values(searchData).some(
      (v) => String(v).trim() !== "",
    );

    if (!hasSearchCriteria) {
      toast.warning("Please enter at least one search criteria!");
      return;
    }

    setLoading(true);
    try {
      const params = {
        enquiryNumber: searchData.enquiryNumber,
        customerName: searchData.customerName,
        customerId: searchData.customerId,
        assetSubTypeId: searchData.assetSubTypeId,
        dob: searchData.doB,
      };

      const res = await axios.get(`${BASE_URL}/api/fullPaymentListing/search`, {
        params,
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      console.log("res:", res.data?.data);
      setRows(data);

      // reset selection after new search
      setSelectedIds(new Set());

      toast.success(`Found ${data.length} record(s)`);
    } catch (err) {
      console.error(err);
      toast.error("Search failed");
      setRows([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Add New
  // -------------------------
  const handleAddNew = () => {
    navigate("/Sales/FullPaymentEntry");
  };

  // -------------------------
  // Checkbox handlers
  // -------------------------
  const getRowId = (r) =>
    Number(r.salesLeadId || r.fld_sales_leadid || r.id || 0);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allIds = useMemo(() => rows.map(getRowId).filter((x) => x > 0), [rows]);

  const isAllSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (isAllSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // -------------------------
  // Delete Selected
  // -------------------------
  const handleDeleteSelected = async () => {
    if (selectedArray.length === 0) {
      toast.warning("Please select at least one row to delete.");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedArray.length} selected record(s)?`,
    );
    if (!ok) return;

    try {
      await axios.delete(`${BASE_URL}/api/fullPaymentListing/deleteSelected`, {
        headers: commonHeaders,
        data: { ids: selectedArray },
      });

      toast.success("Deleted successfully!");

      setRows((prev) => prev.filter((r) => !selectedIds.has(getRowId(r))));
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  // -------------------------
  // Update (Single selection)
  // -------------------------
  const handleUpdateSelected = () => {
    if (selectedArray.length === 0) {
      toast.warning("Please select one row to update.");
      return;
    }

    if (selectedArray.length > 1) {
      toast.warning("Please select only ONE row to update.");
      return;
    }

    const id = selectedArray[0];
    navigate(`/Sales/FullPaymentEntry?id=${id}`);
  };

  const handleUpdate = (row) => {
    const id = getRowId(row);

    if (!id) {
      toast.warning("Invalid record selected.");
      return;
    }

    navigate(`/Sales/FullPaymentEntry?id=${id}`);
  };

  const clearFilters = () => {
    setSearchData({
      enquiryNumber: "",
      customerName: "",
      customerId: "",
      assetSubTypeId: "",
      doB: "",
    });

    setRows([]);
    setSelectedIds(new Set());
  };

  const totals = useMemo(() => {
    const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const completedCount = rows.filter(
      (r) => String(r.paymentStatus || "").toLowerCase() === "completed",
    ).length;

    return { totalAmount, completedCount };
  }, [rows]);

  return (
    <SalesLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">FULL PAYMENT CUSTOMER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Full Payment Customer
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Add New */}
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              {/* Show / Hide Filters */}
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  {/* Clear */}
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  {/* Search */}
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Enquiry Number */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaHashtag className="inline mr-2" />
                    Enquiry Number
                  </label>

                  <input
                    type="text"
                    placeholder="Enter enquiry number"
                    value={searchData.enquiryNumber}
                    onChange={(e) =>
                      handleInputChange("enquiryNumber", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Customer Name */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaUser className="inline mr-2" />
                    Customer Name
                  </label>

                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={searchData.customerName}
                    onChange={(e) =>
                      handleInputChange("customerName", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Customer ID */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaHashtag className="inline mr-2" />
                    Customer ID
                  </label>

                  <input
                    type="text"
                    placeholder="Enter customer ID"
                    value={searchData.customerId}
                    onChange={(e) =>
                      handleInputChange("customerId", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Asset Sub Type */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Asset Sub Type
                  </label>

                  <select
                    value={searchData.assetSubTypeId}
                    onChange={(e) =>
                      handleInputChange("assetSubTypeId", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>

                    {assetSubTypes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* DoB */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>

                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  {/* Sl No */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Sl No
                  </th>

                  {/* Customer ID */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Customer ID
                  </th>

                  {/* Bill No */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Bill No
                  </th>

                  {/* Customer Name */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Customer Name
                  </th>

                  {/* Plot Number */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Plot Number
                  </th>

                  {/* Asset Code */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Code
                  </th>

                  {/* Asset Type / Model */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Type / Model
                  </th>

                  {/* Amount */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Amount
                  </th>

                  {/* Payment Status */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Payment Status
                  </th>

                  {/* Edit */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">
                    View
                  </th>

                  {/* Select All - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody
                className={`${
                  isDark ? "divide-gray-700" : "divide-gray-200"
                } divide-y`}
              >
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className={`px-6 py-10 text-center ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      No data found. Please search.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => {
                    const id = getRowId(r);
                    const checked = selectedIds.has(id);

                    return (
                      <tr
                        key={id || index}
                        className={`${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                        } transition-colors ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                        }`}
                      >
                        {/* Sl No */}
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.slNo ?? index + 1}
                        </td>

                        {/* Customer ID */}
                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-blue-400" : "text-blue-600"
                          }`}
                        >
                          {r.customerId}
                        </td>

                        {/* Bill No */}
                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {r.billNo}
                        </td>

                        {/* Customer Name */}
                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {r.customerName}
                        </td>

                        {/* Plot Number */}
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.plotNumber}
                        </td>

                        {/* Asset Code */}
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.assetCode}
                        </td>

                        {/* Asset Type / Model */}
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {r.assetTypeModel || r.assetSubTypeName || ""}
                        </td>

                        {/* Amount */}
                        <td
                          className={`px-6 py-4 text-sm font-semibold ${
                            isDark ? "text-green-400" : "text-green-600"
                          }`}
                        >
                          {formatAmount(r.amount)}
                        </td>

                        {/* Payment Status */}
                        <td className="px-6 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                              r.paymentStatus,
                            )}`}
                          >
                            {r.paymentStatus}
                          </span>
                        </td>

                        {/* View Only */}
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdate(r)}
                             className={`inline-flex items-center justify-center p-2 rounded-lg transition ${
                            isDark
                              ? "text-blue-400 hover:bg-gray-700"
                              : "text-blue-600 hover:bg-blue-50"
                          }`}
                            title="View"
                          >
                             <FaEye/>
                          </button>
                        </td>

                        {/* SELECT - LAST COLUMN */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ==================== RESULTS SUMMARY ==================== */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark
                ? "bg-gray-800/30 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div>
                <h4
                  className={`font-semibold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Search Results
                </h4>

                <p
                  className={`text-sm mt-1 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Showing {rows.length} record(s) | Selected:{" "}
                  {selectedArray.length}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                {/* Total Amount */}
                <div
                  className={`${
                    isDark ? "bg-green-900/30" : "bg-green-50"
                  } p-3 rounded-lg`}
                >
                  <div className="text-lg font-bold text-green-600">
                    {formatAmount(totals.totalAmount)}
                  </div>

                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Total Amount
                  </div>
                </div>

                {/* Completed */}
                <div
                  className={`${
                    isDark ? "bg-blue-900/30" : "bg-blue-50"
                  } p-3 rounded-lg`}
                >
                  <div className="text-lg font-bold text-blue-600">
                    {totals.completedCount}
                  </div>

                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Completed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default FullPaymentCustomer;
